/**
 * Tela de envio (R19.2/R19.5): seleção por clique **ou** arrastar-e-soltar,
 * com validação amigável de formato/tamanho **antes** do upload e mensagens
 * claras e acionáveis.
 *
 * Acessibilidade (R19.7): a área de soltar é um `<button>` real (foco e Enter/
 * Espaço nativos) que abre o seletor de arquivos; o `<input type=file>` fica
 * associado por rótulo; erros de validação usam `role="alert"`.
 */
import { useCallback, useRef, useState } from "react";

import { tierInfo, type SourceFormat } from "../../config/tiers";
import {
  allowedFormatsLabel,
  formatBytes,
  validateFile,
  type ValidationResult,
} from "../../lib/validation";
import type { Settings } from "../../lib/settings";
import { Alert } from "../Alert";
import { Button } from "../Button";
import { FileIcon, SpinnerIcon, UploadIcon } from "../Icons";

interface UploadScreenProps {
  readonly settings: Settings;
  readonly submitting: boolean;
  readonly submitError: string | null;
  readonly onSubmit: (file: File, format: SourceFormat) => void;
}

export function UploadScreen({ settings, submitting, submitError, onSubmit }: UploadScreenProps) {
  const tier = tierInfo(settings.tier);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);

  const handleFile = useCallback(
    (picked: File) => {
      setFile(picked);
      setValidation(validateFile({ name: picked.name, size: picked.size }, tier));
    },
    [tier],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (picked) {
      handleFile(picked);
    }
  };

  const openPicker = () => inputRef.current?.click();

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const picked = e.dataTransfer.files?.[0];
    if (picked) {
      handleFile(picked);
    }
  };

  const canSubmit = file !== null && validation?.ok === true && !submitting;

  const submit = () => {
    if (file && validation?.ok) {
      onSubmit(file, validation.format);
    }
  };

  const acceptAttr = tier.formats.map((f) => `.${f}`).join(",");

  return (
    <section className="mp-card" aria-labelledby="upload-title">
      <h1 className="mp-card__title" id="upload-title">
        Publique seu EPUB
      </h1>
      <p className="mp-card__subtitle">
        Envie seu documento e receba um livro digital profissional, validado e acessível.
      </p>

      <button
        type="button"
        className={`mp-dropzone${dragActive ? " mp-dropzone--active" : ""}`}
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        aria-label="Escolher arquivo ou arrastar e soltar aqui"
      >
        <span className="mp-dropzone__icon">
          <UploadIcon size={40} />
        </span>
        <span>
          <strong>Clique para escolher</strong> ou arraste o arquivo aqui
        </span>
        <span className="mp-dropzone__hint">
          Formatos aceitos no plano {tier.label}: {allowedFormatsLabel(tier)} · até{" "}
          {formatBytes(tier.maxFileBytes)}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        onChange={onInputChange}
        aria-hidden="true"
        tabIndex={-1}
      />

      {file ? (
        <div className="mp-file">
          <span className="mp-file__icon">
            <FileIcon size={28} />
          </span>
          <span className="mp-file__meta">
            <span className="mp-file__name">{file.name}</span>
            <span className="mp-file__size">{formatBytes(file.size)}</span>
          </span>
        </div>
      ) : null}

      {validation && !validation.ok ? (
        <Alert tone="warning" title="Verifique o arquivo">
          {validation.message}
        </Alert>
      ) : null}

      {submitError ? (
        <Alert tone="error" title="Não foi possível iniciar o envio">
          {submitError}
        </Alert>
      ) : null}

      <div className="mp-actions">
        <Button
          variant="primary"
          className="mp-btn--block"
          onClick={submit}
          disabled={!canSubmit}
          loading={submitting}
        >
          {submitting ? (
            <>
              <SpinnerIcon size={18} /> Enviando…
            </>
          ) : (
            <>
              <UploadIcon size={18} /> Converter para EPUB
            </>
          )}
        </Button>
      </div>
    </section>
  );
}
